import { useEffect, useState } from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../features/auth/use-auth.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Label } from '../components/ui/label.js';
import { getAiCatalog, getAiSettings, updateAiSettings } from '../features/ai-settings/api.js';
import { describeModel } from '../features/ai-settings/describe-model.js';
import {
  FAMILY_LABEL,
  LATEST_PREFIX,
  SLOT_RECOMMENDED_MODEL,
  SLOT_SUBTITLE_HU,
  SLOT_TITLE_HU,
  type AiSettings,
  type LogicalSlot,
  type ModelCatalog,
  type ModelFamily,
  type UpdateAiSettingsBody,
} from '../features/ai-settings/types.js';

const SLOTS: LogicalSlot[] = ['HEAVY', 'ROUTINE', 'INTAKE', 'CHEAP'];

const PROVIDER_LABEL: Record<AiSettings['provider'], string> = {
  openrouter: 'OpenRouter',
  dryrun: 'Dry-run (stub)',
};

const DEFAULT_VALUE = '__default__';

interface PickerValue {
  selection: string;
}

function pickerFromOverride(override: string | null): PickerValue {
  if (override == null) return { selection: DEFAULT_VALUE };
  return { selection: override };
}

function pickerToOverride(v: PickerValue): string | null {
  if (v.selection === DEFAULT_VALUE) return null;
  return v.selection;
}

const FAMILY_ORDER: ModelFamily[] = ['anthropic', 'openai', 'google', 'other'];

function SlotCard({
  slot,
  settings,
  catalog,
  value,
  onChange,
}: {
  slot: LogicalSlot;
  settings: AiSettings;
  catalog: ModelCatalog;
  value: PickerValue;
  onChange: (v: PickerValue) => void;
}) {
  const override =
    slot === 'HEAVY'
      ? settings.modelHeavy
      : slot === 'ROUTINE'
        ? settings.modelRoutine
        : slot === 'INTAKE'
          ? settings.modelIntake
          : settings.modelCheap;
  const effective = settings.effective[slot];
  const configured = settings.configured[slot];
  const recommendedId = SLOT_RECOMMENDED_MODEL[slot];
  // An override saved before a catalog refresh (or set straight in the DB) may
  // not be in the current catalog. Without an <option> for it the select would
  // silently display the first entry instead, and saving would overwrite the
  // override with a model nobody picked — so render it as its own entry.
  const known = (id: string) =>
    catalog.latest.some((o) => o.id === id) || catalog.models.some((m) => m.id === id);
  const orphanSelection =
    value.selection !== DEFAULT_VALUE && !known(value.selection) ? value.selection : null;
  const families = FAMILY_ORDER.filter((f) => catalog.models.some((m) => m.family === f));

  return (
    <Card className="flex flex-col gap-4 p-5">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="font-sans text-h2 font-semibold leading-snug tracking-tight text-[var(--ink-700)]">
            {SLOT_TITLE_HU[slot]}
          </div>
          <p className="mt-1 text-meta leading-snug text-[var(--fg-2)]">{SLOT_SUBTITLE_HU[slot]}</p>
        </div>
        {override == null ? (
          <span className="inline-flex items-center rounded-full bg-[var(--app-divider)] px-2.5 py-0.5 text-meta font-medium uppercase tracking-wider text-[var(--fg-2)]">
            Alap
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-[var(--brand-100)] px-2.5 py-0.5 text-meta font-semibold uppercase tracking-wider text-[var(--brand-800)]">
            Felülírva
          </span>
        )}
      </header>

      <div>
        <div className="text-meta uppercase tracking-wider text-[var(--fg-faint)]">
          Aktív modell
        </div>
        <code className="mt-1 block break-all rounded-md bg-[var(--app-bg)] px-2 py-1.5 font-mono text-meta leading-snug text-[var(--ink-700)]">
          {effective}
        </code>
        {configured.startsWith(LATEST_PREFIX) ? (
          <p className="mt-1 text-meta text-[var(--fg-2)]">
            {describeModel(configured, catalog)} — új verziónál magától vált.
          </p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`picker-${slot}`}>Modell</Label>
        <select
          id={`picker-${slot}`}
          value={value.selection}
          onChange={(e) => onChange({ selection: e.target.value })}
          className="flex h-9 w-full rounded-md border border-[var(--app-divider-2)] bg-[var(--n-0)] px-3 text-body text-[var(--ink-700)] focus:border-[var(--brand-500)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-500)]/30"
        >
          <option value={DEFAULT_VALUE}>
            Alapértelmezett ({describeModel(settings.defaults[slot], catalog)})
          </option>
          {orphanSelection ? (
            <option value={orphanSelection}>{orphanSelection} · Egyedi</option>
          ) : null}
          <optgroup label="Mindig a legújabb">
            {catalog.latest.map((o) => (
              <option key={o.id} value={o.id}>
                Legújabb {o.label}
                {o.resolvesTo ? ` (most: ${o.resolvesTo.split('/')[1]})` : ''}
                {o.id === recommendedId ? ' · Ajánlott' : ''}
              </option>
            ))}
          </optgroup>
          {families.map((f) => (
            <optgroup key={f} label={`Rögzített verzió — ${FAMILY_LABEL[f]}`}>
              {catalog.models
                .filter((m) => m.family === f)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </div>
    </Card>
  );
}

function AdminAiPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const settings = useQuery<AiSettings>({
    queryKey: ['ai-settings'],
    queryFn: getAiSettings,
  });
  const catalog = useQuery<ModelCatalog>({
    queryKey: ['ai-catalog'],
    queryFn: getAiCatalog,
  });

  const [formState, setFormState] = useState<Record<LogicalSlot, PickerValue> | null>(null);

  useEffect(() => {
    if (!settings.data || !catalog.data) return;
    setFormState({
      HEAVY: pickerFromOverride(settings.data.modelHeavy),
      ROUTINE: pickerFromOverride(settings.data.modelRoutine),
      INTAKE: pickerFromOverride(settings.data.modelIntake),
      CHEAP: pickerFromOverride(settings.data.modelCheap),
    });
  }, [settings.data, catalog.data]);

  const save = useMutation({
    mutationFn: (body: UpdateAiSettingsBody) => updateAiSettings(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ai-settings'] });
    },
  });

  if (user && user.role !== 'admin') {
    return <Navigate to="/app" />;
  }
  if (settings.isLoading || catalog.isLoading || !settings.data || !catalog.data || !formState) {
    return <p className="text-body text-[var(--fg-2)]">Betöltés…</p>;
  }

  const dto = settings.data;

  function onSubmit() {
    if (!formState) return;
    save.mutate({
      modelHeavy: pickerToOverride(formState.HEAVY),
      modelRoutine: pickerToOverride(formState.ROUTINE),
      modelIntake: pickerToOverride(formState.INTAKE),
      modelCheap: pickerToOverride(formState.CHEAP),
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-sans text-h1 font-normal leading-tight tracking-[-0.02em] text-[var(--ink-700)]">
          AI beállítások
        </h1>
        <p className="mt-1 max-w-[680px] text-body text-[var(--fg-2)]">
          Válaszd ki AI funkciónként, melyik modell dolgozzon. A „Mindig a legújabb” opció új
          modellverzió megjelenésekor magától vált; a rögzített verzió nem. A változás minden
          további AI hívásra érvényes — futó job-okat nem érint.
        </p>
        {catalog.data.source === 'fallback' ? (
          <p role="status" className="mt-2 max-w-[680px] text-meta text-[var(--fg-2)]">
            Az OpenRouter modell-listája most nem érhető el, ezért a tartaléklistát látod. A
            beállítás így is menthető.
          </p>
        ) : null}
      </div>

      <Card className="p-5">
        <div className="mb-1 text-meta uppercase tracking-wider text-[var(--fg-faint)]">
          Provider állapot
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <div className="text-meta uppercase tracking-wider text-[var(--fg-faint)]">
              Provider
            </div>
            <div className="font-sans text-h2 font-medium text-[var(--ink-700)]">
              {PROVIDER_LABEL[dto.provider]}
            </div>
          </div>
          <div>
            <div className="text-meta uppercase tracking-wider text-[var(--fg-faint)]">
              AI engedélyezve
            </div>
            <div className="font-sans text-h2 font-medium text-[var(--ink-700)]">
              {dto.aiEnabled ? 'Igen' : 'Nem'}
            </div>
          </div>
          <div>
            <div className="text-meta uppercase tracking-wider text-[var(--fg-faint)]">
              Base URL
            </div>
            <code className="block truncate font-mono text-body text-[var(--ink-700)]">
              {dto.baseUrl ?? '—'}
            </code>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {SLOTS.map((slot) => (
          <SlotCard
            key={slot}
            slot={slot}
            settings={dto}
            catalog={catalog.data!}
            value={formState[slot]}
            onChange={(next) => setFormState((s) => (s ? { ...s, [slot]: next } : s))}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {save.isError && (
          <span className="text-body text-[var(--danger)]">
            Mentési hiba: {(save.error as Error).message}
          </span>
        )}
        {save.isSuccess && <span className="text-body text-[var(--brand-700)]">Mentve.</span>}
        {dto.updatedAt && (
          <span className="text-meta text-[var(--fg-faint)]">
            Utolsó módosítás: {new Date(dto.updatedAt).toLocaleString('hu-HU')}
          </span>
        )}
        <Button onClick={onSubmit} disabled={save.isPending}>
          {save.isPending ? 'Mentés…' : 'Mentés'}
        </Button>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/admin/ai')({
  component: AdminAiPage,
});
